import './ServiceResponseDetails.scss';

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { message } from 'antd';
import { useParams } from 'react-router-dom';

import Header from '../../../../components/Header/Header';
import ConfirmationModal
  from '../../../../components/Modals/ConfirmationModal/ConfirmationModal';
import Loader from '../../../../components/V2/Loader/Loader';
import {
  resetCreateOrViewOfferModalState,
  setCreateOrViewOfferModalState,
} from '../../../../context/tqConnectServiceRequestReducer';
import {
  useUpdateTQServiceRequestResponse,
} from '../../../../hooks/mutations/tqConnectMutations/service-requests';
import {
  useTQConnectServiceRequestById,
  useTQConnectServiceRequestResponses,
} from '../../../../hooks/queries/tqConnectQueries/service-requests';
import {
  useAppDispatch,
  useAppNavigate,
  useAppSelector,
} from '../../../../shared/hooks';
import {
  ITQCreateServiceRequestResponseBody,
  ITQServiceRequestResponseDetailsUser,
  TQServiceRequestStatus,
} from '../../../../types/tqConnectTypes/service-requests';
import TQServiceRequestResponseDetails
  from '../../components/TQServiceRequestResponseBottpmSheet/TQServiceRequestResponseDetails';
import ResponseAvatarsStrip from './ResponseAvatarsStrip';

const initialFormValues: ITQCreateServiceRequestResponseBody = {
  description: '',
  attachments: [],
};

const ServiceResponseDetails = () => {
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();
  const { serviceRequestId } = useParams<{ serviceRequestId: string }>();
  const query = new URLSearchParams(window.location.search);
  const responseId = query.get('responseId');

  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);

  const { createOrViewOfferModalState } = useAppSelector(
    (state) => state.tqConnectServiceRequest,
  );

  const [selectedResponseId, setSelectedResponseId] = useState<string | null>(
    null,
  );

  const { response, canAcceptOrReject } = createOrViewOfferModalState;

  const { data: serviceRequestDetails, isLoading: loading } =
    useTQConnectServiceRequestById(serviceRequestId || '', false);

  const { updateServiceRequestResponse, isPending: isAcceptingOrRejecting } =
    useUpdateTQServiceRequestResponse(
      serviceRequestId || '',
      response?._id || '',
    );
  const { data: serviceRequestResponsesResult } =
    useTQConnectServiceRequestResponses(serviceRequestId || '');

  const { list } = useMemo(() => {
    return {
      list: serviceRequestResponsesResult?.data ?? [],
      total: serviceRequestResponsesResult?.total ?? 0,
    };
  }, [
    serviceRequestResponsesResult?.data,
    serviceRequestResponsesResult?.total,
  ]);

  const reset = useCallback(() => {
    setOpenConfirmationModal(false);
    dispatch(resetCreateOrViewOfferModalState());
    setSelectedResponseId(null);
  }, [dispatch]);

  const acceptOrRejectServiceRequest = async (
    status: 'accepted' | 'rejected',
  ) => {
    updateServiceRequestResponse(
      {
        serviceRequestId: serviceRequestId || '',
        serviceRequestResponseId: response?._id || '',
        data: { status },
      },
      {
        onSuccess: (resp) => {
          const updatedResponse = resp.data.result;
          message.success(`Offer ${status}`);
          dispatch(
            setCreateOrViewOfferModalState({
              isOpen: true,
              mode: 'view',
              serviceRequestId,
              canAcceptOrReject: true,
              respondedBy: response?.respondedBy,
              response: {
                ...updatedResponse,
                respondedBy:
                  response?.respondedBy as ITQServiceRequestResponseDetailsUser,
              },
            }),
          );
        },
      },
    );
  };

  useEffect(() => {
    if (selectedResponseId && !response && responseId && list.length > 0) {
      const selectedResponse = list.find(
        (resp) => resp._id === selectedResponseId,
      );
      const newResponse = selectedResponse || list?.[0];
      dispatch(
        setCreateOrViewOfferModalState({
          isOpen: true,
          mode: 'view',
          serviceRequestId,
          canAcceptOrReject: true,
          respondedBy: newResponse?.respondedBy,
          response: newResponse,
        }),
      );
    }
  }, [
    dispatch,
    list,
    response,
    responseId,
    selectedResponseId,
    serviceRequestId,
  ]);

  useEffect(() => {
    if (!serviceRequestId) {
      navigate.goBack();
    } else if (responseId) {
      setSelectedResponseId(responseId);
    }
  }, [navigate, serviceRequestId, responseId]);

  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  const handleSelectResponse = useCallback(
    (selectedResponse: (typeof list)[0]) => {
      setSelectedResponseId(selectedResponse._id);
      dispatch(
        setCreateOrViewOfferModalState({
          isOpen: true,
          mode: 'view',
          serviceRequestId,
          canAcceptOrReject: true,
          respondedBy: selectedResponse.respondedBy,
          response: selectedResponse,
        }),
      );
    },
    [dispatch, serviceRequestId],
  );

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="service-response-details">
      <Header title="Response details" handleBack={() => navigate.goBack()} />
      <TQServiceRequestResponseDetails
        mode={'view'}
        canAcceptOrReject={
          canAcceptOrReject &&
          serviceRequestDetails?.status === TQServiceRequestStatus.OPEN
        }
        serviceRequestId={serviceRequestId || ''}
        respondedBy={response?.respondedBy}
        response={response}
        formValues={initialFormValues}
        onChangeFormValues={() => {}}
        attachments={[]}
        setAttachments={() => {}}
        serviceRequestDetails={serviceRequestDetails}
        onSuccess={() => {}}
        onAccept={() => setOpenConfirmationModal(true)}
        onReject={() => acceptOrRejectServiceRequest('rejected')}
        isRejecting={isAcceptingOrRejecting}
        reset={() => {}}
      />
      <ResponseAvatarsStrip
        responses={list}
        selectedResponseId={response?._id ?? null}
        onSelectResponse={handleSelectResponse}
      />
      <div className="service-response-details__content">
        <ConfirmationModal
          title="Move forward with this offer"
          message="By accepting this offer, you’ll lock in this collaboration and automatically decline all other offers. Ready to proceed?"
          handleOk={() => {
            acceptOrRejectServiceRequest('accepted');
            setOpenConfirmationModal(false);
          }}
          handleCancel={() => setOpenConfirmationModal(false)}
          open={openConfirmationModal}
          okayButtonText="Yes, accept"
          cancelButtonText="Back"
          loading={isAcceptingOrRejecting}
        />
      </div>
    </div>
  );
};

export default ServiceResponseDetails;
